import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, IconButton, TextField,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Grid, Switch, FormControlLabel
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, Home, Category, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';

const API = process.env.REACT_APP_API_URL;
const APPLIES = ['Both', 'Boarder', 'Day Scholar'];
const emptyForm = { code: '', name: '', glAccount: '', sequence: '', applicableTo: 'Both', isActive: true, isVatable: false, vatPostingGroup: '' };

const ChargeItemList = () => {
  const navigate = useNavigate();
  const { accounts: glAccounts } = useGLAccounts();
  const [items,      setItems]      = useState([]);
  const [vatGroups,  setVatGroups]  = useState([]);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetch = useCallback(async () => {
    try {
      const [ciRes, vatRes] = await Promise.all([
        axios.get(`${API}/api/academics/charge-items`, { headers }),
        axios.get(`${API}/api/academics/vat-posting-groups`, { headers }),
      ]);
      setItems(ciRes.data);
      setVatGroups(vatRes.data);
    } catch { setError('Failed to load charge items'); }
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => {
    const nextSeq = items.length > 0 ? Math.max(...items.map(i => i.sequence)) + 1 : 1;
    setEditing(null);
    setForm({ ...emptyForm, sequence: nextSeq });
    setError(''); setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ code: item.code, name: item.name, glAccount: item.glAccount || '',
              sequence: item.sequence, applicableTo: item.applicableTo, isActive: item.isActive,
              isVatable: item.isVatable || false, vatPostingGroup: item.vatPostingGroup || '' });
    setError(''); setDialogOpen(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.code.trim())   { setError('Code is required'); return; }
    if (!form.name.trim())   { setError('Name is required'); return; }
    if (!form.sequence)      { setError('Sequence is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await axios.put(`${API}/api/academics/charge-items/${editing._id}`, {
          name: form.name, glAccount: form.glAccount,
          sequence: parseInt(form.sequence, 10),
          applicableTo: form.applicableTo, isActive: form.isActive,
          isVatable: form.isVatable, vatPostingGroup: form.isVatable ? form.vatPostingGroup : ''
        }, { headers });
        setSuccess('Updated');
      } else {
        await axios.post(`${API}/api/academics/charge-items`, {
          code: form.code.toUpperCase(), name: form.name,
          glAccount: form.glAccount,
          sequence: parseInt(form.sequence, 10),
          applicableTo: form.applicableTo,
          isVatable: form.isVatable, vatPostingGroup: form.isVatable ? form.vatPostingGroup : ''
        }, { headers });
        setSuccess('Created');
      }
      setDialogOpen(false); fetch();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = (item) => setConfirmDlg(item);

  const confirmDelete = async () => {
    const item = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/academics/charge-items/${item._id}`, { headers });
      setSuccess('Deleted'); fetch();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const appColor = { Both: 'default', Boarder: 'primary', 'Day Scholar': 'success' };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Category color="primary" />
              <Typography variant="h5" color="primary">Charge Items</Typography>
              <Chip label={items.length} size="small" color="primary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
                Back
              </Button>
              <Tooltip title="Home">
                <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Item</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Charge items define the types of fees charged to learners. They control the payment allocation sequence — lower sequence = applied first.
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell align="center"><strong>Seq</strong></TableCell>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>GL Account</strong></TableCell>
                <TableCell><strong>Applies To</strong></TableCell>
                <TableCell align="center"><strong>VAT</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No charge items yet. Run the seed script or add manually.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : items.map(item => (
                <TableRow key={item._id} hover>
                  <TableCell align="center">
                    <Chip label={item.sequence} size="small" sx={{ minWidth: 36 }} />
                  </TableCell>
                  <TableCell><strong>{item.code}</strong></TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    {item.glAccount
                      ? <Typography variant="caption" fontFamily="monospace">{item.glAccount}</Typography>
                      : <em style={{ color: '#bbb', fontSize: 12 }}>Not set</em>}
                  </TableCell>
                  <TableCell>
                    <Chip label={item.applicableTo} size="small"
                      color={appColor[item.applicableTo] || 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    {item.isVatable
                      ? <Chip label={item.vatPostingGroup || 'VAT'} size="small" color="warning" variant="outlined" />
                      : <em style={{ color: '#bbb', fontSize: 12 }}>—</em>}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={item.isActive ? 'Active' : 'Inactive'}
                      color={item.isActive ? 'success' : 'default'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(item)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(item)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete charge item <strong>{confirmDlg?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.code}` : 'Add Charge Item'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Code *" name="code" value={form.code} onChange={handle}
                fullWidth size="small" disabled={!!editing}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText="e.g. TUI" />
            </Grid>
            <Grid item xs={5}>
              <TextField label="Name *" name="name" value={form.name}
                onChange={handle} fullWidth size="small" helperText="e.g. Tuition Fee" />
            </Grid>
            <Grid item xs={3}>
              <TextField label="Sequence *" name="sequence" type="number" value={form.sequence}
                onChange={handle} fullWidth size="small"
                inputProps={{ min: 1 }}
                helperText="Payment order" />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Applies To" name="applicableTo" value={form.applicableTo}
                onChange={handle} fullWidth size="small">
                {APPLIES.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select label="GL Account (Credit side of invoice)" name="glAccount" value={form.glAccount}
                onChange={handle} fullWidth size="small"
                helperText="Income/Revenue for earned fees · Liability (Deferred) for pre-payments received before service">
                {glAccountOptions(glAccounts, { noneLabel: '— Not set —' })}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={form.isVatable}
                  onChange={e => setForm(f => ({ ...f, isVatable: e.target.checked, vatPostingGroup: '' }))} />}
                label="VAT Applicable" />
            </Grid>
            {form.isVatable && (
              <Grid item xs={6}>
                <TextField select label="VAT Posting Group" name="vatPostingGroup"
                  value={form.vatPostingGroup} onChange={handle} fullWidth size="small">
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  {vatGroups.map(v => (
                    <MenuItem key={v.code} value={v.code}>{v.code} — {v.name} ({v.vatPercent}%)</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {editing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.isActive}
                    onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                  label={form.isActive ? 'Active' : 'Inactive'} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChargeItemList;
